import { useEffect, useRef } from 'react';
import {
  startLearningSession,
  heartbeatLearningSession,
  endLearningSession,
  HEARTBEAT_MS,
} from '../utils/sessionHelpers';

// Starts a learning session when orgId/userId/lessonId are available, heartbeats
// on an interval, and ends the session on unmount or whenever lessonId changes
// (the effect cleanup tears down the old session before the new one starts).
export const useLearningSession = ({ orgId, userId, courseId, lessonId }) => {
  const sessionIdRef = useRef(null);

  useEffect(() => {
    if (!orgId || !userId || !lessonId) return;

    let cancelled = false;
    let intervalId = null;

    const begin = async () => {
      const { sessionId } = await startLearningSession({ orgId, userId, courseId, lessonId });
      if (cancelled) {
        if (sessionId) endLearningSession(sessionId).catch(() => {});
        return;
      }
      sessionIdRef.current = sessionId;
      if (sessionId) {
        intervalId = setInterval(() => {
          heartbeatLearningSession(sessionId).catch(() => {});
        }, HEARTBEAT_MS);
      }
    };

    begin();

    // React's cleanup doesn't run on tab close / hard navigation, so end the
    // session on beforeunload too — otherwise it's left with endedAt: null.
    const handleUnload = () => {
      if (sessionIdRef.current) endLearningSession(sessionIdRef.current).catch(() => {});
    };
    window.addEventListener('beforeunload', handleUnload);

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      window.removeEventListener('beforeunload', handleUnload);
      if (sessionIdRef.current) {
        endLearningSession(sessionIdRef.current).catch(() => {});
        sessionIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, userId, courseId, lessonId]);
};